import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../components/Layout/MainLayout.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.jsx';
import { Button } from '../components/ui/button.jsx';
import { Input } from '../components/ui/input.jsx';
import { Label } from '../components/ui/label.jsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select.jsx';
import { CheckCircle2, Save, Calculator, AlertCircle, Info, Plus, Trash2 } from 'lucide-react';
import CargoAutocomplete from '../components/CargoAutocomplete.js';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const MODULE_LABELS = { hr: 'Indicadores RH', esg: 'ESG & Pessoas', salary: 'Remuneração', benefits: 'Benefícios', headcount_detail: 'Headcount Detalhado' };
const MESES = [
  { value: '01', label: 'Janeiro' }, { value: '02', label: 'Fevereiro' }, { value: '03', label: 'Março' },
  { value: '04', label: 'Abril' }, { value: '05', label: 'Maio' }, { value: '06', label: 'Junho' },
  { value: '07', label: 'Julho' }, { value: '08', label: 'Agosto' }, { value: '09', label: 'Setembro' },
  { value: '10', label: 'Outubro' }, { value: '11', label: 'Novembro' }, { value: '12', label: 'Dezembro' },
];

const REMUN_FIELDS = [
  { key: 'horas_extras', label: 'Horas Extras (R$/mês)' },
  { key: 'adicional_noturno', label: 'Adicional Noturno (R$/mês)' },
  { key: 'adicional_periculosidade', label: 'Adic. Periculosidade (R$/mês)' },
  { key: 'adicional_insalubridade', label: 'Adic. Insalubridade (R$/mês)' },
];

const SALARY_ROLES = [
  'Analista de RH', 'Coordenador de RH', 'Gerente de RH',
  'Analista Financeiro', 'Controller', 'Diretor Financeiro',
  'Desenvolvedor', 'Tech Lead', 'Gerente de TI',
  'Analista de Marketing', 'Gerente de Marketing',
  'Analista Comercial', 'Gerente Comercial',
  'Engenheiro de Produção', 'Gerente de Operações',
];

const BENEFIT_FIELDS = [
  { key: 'plano_saude', label: 'Plano de Saúde (R$/mês)' },
  { key: 'plano_odontologico', label: 'Plano Odontológico (R$/mês)' },
  { key: 'vale_alimentacao', label: 'Vale Alimentação (R$/mês)' },
  { key: 'vale_refeicao', label: 'Vale Refeição (R$/mês)' },
  { key: 'vale_transporte', label: 'Vale Transporte (R$/mês)' },
  { key: 'seguro_vida', label: 'Seguro de Vida (R$/mês)' },
  { key: 'auxilio_educacao', label: 'Auxílio Educação (R$/mês)' },
  { key: 'gympass', label: 'Gympass (R$/mês)' },
  { key: 'previdencia_privada', label: 'Previdência Privada (R$/mês)' },
  { key: 'ppr_plr', label: 'PPR/PLR (R$/ano)' },
];

// ---- Computed field with auto-calculated percentage ----
const CalcField = ({ label, numKey, numLabel, denomKey, denomLabel, data, onChange, pctLabel, unit = '%', syncHint = null }) => {
  const num = parseFloat(data[numKey]) || 0;
  const denom = parseFloat(data[denomKey]) || 0;
  const pct = denom > 0 ? ((num / denom) * 100).toFixed(2) : '—';

  return (
    <div className="bg-slate-50 rounded-lg p-3 space-y-2" data-testid={`calc-field-${numKey}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs font-medium text-slate-700">{label}</p>
        {syncHint && (
          <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" data-testid={`sync-hint-${numKey}`}>
            {syncHint}
          </span>
        )}
      </div>
      <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
        <div>
          <Label className="text-[11px] text-slate-500">{numLabel}</Label>
          <Input type="number" value={data[numKey] || ''} onChange={e => onChange(numKey, e.target.value)} className="h-8 text-sm" data-testid={`input-${numKey}`} />
        </div>
        <div>
          <Label className="text-[11px] text-slate-500">{denomLabel}</Label>
          <Input type="number" value={data[denomKey] || ''} onChange={e => onChange(denomKey, e.target.value)} className="h-8 text-sm bg-white" data-testid={`input-${denomKey}-for-${numKey}`} disabled={denomKey === 'total_funcionarios'} />
        </div>
        <div className="flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1.5 rounded text-sm font-semibold min-w-[80px] justify-center" data-testid={`pct-${numKey}`}>
          <Calculator className="w-3 h-3" />
          {pct}{pct !== '—' ? unit : ''}
        </div>
      </div>
      {pctLabel && <p className="text-[10px] text-slate-400">{pctLabel}</p>}
    </div>
  );
};

const SimpleField = ({ label, fieldKey, data, onChange, suffix = '' }) => (
  <div className="space-y-1">
    <Label className="text-xs">{label}</Label>
    <Input type="number" step="any" value={data[fieldKey] || ''} onChange={e => onChange(fieldKey, e.target.value)} className="h-9" data-testid={`input-${fieldKey}`} />
    {suffix && <p className="text-[10px] text-slate-400">{suffix}</p>}
  </div>
);

const PeriodSelector = ({ type, mes, setMes, ano, setAno }) => (
  <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="period-selector">
    <span className="text-xs font-medium text-slate-600">Período:</span>
    {type === 'mensal' && (
      <Select value={mes} onValueChange={setMes}>
        <SelectTrigger className="w-[140px] h-8 text-sm" data-testid="select-mes"><SelectValue placeholder="Mês" /></SelectTrigger>
        <SelectContent>
          {MESES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
        </SelectContent>
      </Select>
    )}
    <Select value={String(ano)} onValueChange={v => setAno(parseInt(v))}>
      <SelectTrigger className="w-[100px] h-8 text-sm" data-testid="select-ano"><SelectValue placeholder="Ano" /></SelectTrigger>
      <SelectContent>
        {[2023, 2024, 2025, 2026].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
      </SelectContent>
    </Select>
    <span className="text-[10px] text-slate-400 ml-1">{type === 'mensal' ? 'Coleta mensal' : 'Coleta anual'}</span>
  </div>
);

const ResponderPesquisa = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [hrData, setHrData] = useState({});
  const [esgData, setEsgData] = useState({});
  const [salaryData, setSalaryData] = useState([]);
  const [benefitsData, setBenefitsData] = useState({});
  const [submittedModules, setSubmittedModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hrMes, setHrMes] = useState('01');
  const [hrAno, setHrAno] = useState(2024);
  const [esgMes, setEsgMes] = useState('01');
  const [esgAno, setEsgAno] = useState(2024);
  const [salaryAno, setSalaryAno] = useState(2024);
  const [benefitsAno, setBenefitsAno] = useState(2024);
  const [headcountRows, setHeadcountRows] = useState([]);
  const [headcountMes, setHeadcountMes] = useState('01');
  const [headcountAno, setHeadcountAno] = useState(2024);
  const [surveyCargos, setSurveyCargos] = useState([]);
  const [pendingCargoUpdates, setPendingCargoUpdates] = useState([]);
  const userRole = localStorage.getItem('userRole') || 'respondent';

  // Pares de campos que representam o mesmo conceito em RH e ESG.
  // Sincronizam automaticamente para evitar dupla digitação.
  const HR_ESG_SYNC = [
    { hr: 'total_funcionarios', esg: 'total_colaboradores', label: 'Total de pessoas' },
    { hr: 'qtd_feminino', esg: 'qtd_mulheres', label: 'Quantidade de mulheres' },
    { hr: 'qtd_pcd', esg: 'qtd_pcd', label: 'Quantidade de PCD' },
  ];

  const hrOnChange = (k, v) => {
    setHrData(p => ({ ...p, [k]: v }));
    const pair = HR_ESG_SYNC.find(s => s.hr === k);
    if (pair) {
      setEsgData(p => String(p[pair.esg] ?? '') === String(v ?? '') ? p : ({ ...p, [pair.esg]: v }));
    }
  };
  const esgOnChange = (k, v) => {
    setEsgData(p => ({ ...p, [k]: v }));
    const pair = HR_ESG_SYNC.find(s => s.esg === k);
    if (pair) {
      setHrData(p => String(p[pair.hr] ?? '') === String(v ?? '') ? p : ({ ...p, [pair.hr]: v }));
    }
  };

  const isSynced = (hrKey, esgKey) => {
    const a = hrData[hrKey];
    const b = esgData[esgKey];
    return a !== undefined && a !== '' && b !== undefined && b !== '' && String(a) === String(b);
  };
  const headcountSynced = isSynced('total_funcionarios', 'total_colaboradores');
  const femaleSynced = isSynced('qtd_feminino', 'qtd_mulheres');
  const pcdSynced = isSynced('qtd_pcd', 'qtd_pcd');

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/surveys/${surveyId}`, { headers: getHeaders() });
        setSurvey(data);
        if (data.periodo) {
          setHrAno(data.periodo);
          setEsgAno(data.periodo);
          setSalaryAno(data.periodo);
          setBenefitsAno(data.periodo);
        }

        // Store survey-defined cargos
        const sCargos = data.cargos || [];
        setSurveyCargos(sCargos);

        // Store pending cargo updates
        setPendingCargoUpdates(data.pending_cargo_updates || []);

        const existing = data.my_responses || [];
        setSubmittedModules(existing.map(r => r.modulo));
        existing.forEach(r => {
          if (r.modulo === 'hr') { setHrData(r.dados || {}); if (r.dados?.mes) setHrMes(r.dados.mes); if (r.dados?.ano) setHrAno(r.dados.ano); }
          if (r.modulo === 'esg') { setEsgData(r.dados || {}); if (r.dados?.mes) setEsgMes(r.dados.mes); if (r.dados?.ano) setEsgAno(r.dados.ano); }
          if (r.modulo === 'salary') { setSalaryData(r.dados?.cargos || []); if (r.dados?.ano) setSalaryAno(r.dados.ano); }
          if (r.modulo === 'benefits') { setBenefitsData(r.dados || {}); if (r.dados?.ano) setBenefitsAno(r.dados.ano); }
          if (r.modulo === 'headcount_detail') {
            setHeadcountRows(r.dados?.rows || []);
            if (r.dados?.mes) setHeadcountMes(r.dados.mes);
            if (r.dados?.ano) setHeadcountAno(r.dados.ano);
          }
        });

        // Initialize salary rows from survey cargos (if no existing response)
        if (data.modulos?.includes('salary') && existing.findIndex(r => r.modulo === 'salary') < 0) {
          const customFields = data.campos_remuneracao || [];
          if (sCargos.length > 0) {
            setSalaryData(sCargos.map(c => ({
              cargo: c.nome, descricao_sumaria: c.descricao_sumaria || '',
              salario_base: '', remuneracao_variavel: '', qtd_ocupantes: '',
              horas_extras: '', adicional_noturno: '', adicional_periculosidade: '', adicional_insalubridade: '',
              outros_remuneracao: customFields.map(f => ({ descricao: f, valor: '' })),
            })));
          } else {
            setSalaryData(SALARY_ROLES.slice(0, 5).map(name => ({
              cargo: name, salario_base: '', remuneracao_variavel: '', qtd_ocupantes: '',
              horas_extras: '', adicional_noturno: '', adicional_periculosidade: '', adicional_insalubridade: '',
              outros_remuneracao: customFields.map(f => ({ descricao: f, valor: '' })),
            })));
          }
        }
      } catch (e) { toast.error('Erro ao carregar pesquisa'); }
    };
    load();
  }, [surveyId]);

  const buildHrPayload = () => {
    const total = parseFloat(hrData.total_funcionarios) || 0;
    const calc = (numKey) => {
      const num = parseFloat(hrData[numKey]) || 0;
      return total > 0 ? parseFloat(((num / total) * 100).toFixed(2)) : 0;
    };
    return {
      ...hrData, mes: hrMes, ano: hrAno,
      turnover_geral_pct: calc('demissoes_total'),
      turnover_voluntario_pct: calc('demissoes_voluntarias'),
      turnover_involuntario_pct: calc('demissoes_involuntarias'),
      absenteismo_pct: total > 0 ? parseFloat(((parseFloat(hrData.dias_ausencia || 0) / (total * 22)) * 100).toFixed(2)) : 0,
      headcount_feminino_pct: calc('qtd_feminino'),
      pcd_pct: calc('qtd_pcd'),
      treinamento_horas_per_capita: total > 0 ? parseFloat((parseFloat(hrData.total_horas_treinamento || 0) / total).toFixed(1)) : 0,
    };
  };

  const buildEsgPayload = () => {
    const total = parseFloat(esgData.total_colaboradores) || 0;
    const totalLid = parseFloat(esgData.total_lideranca) || 0;
    const calc = (numKey, base) => {
      const num = parseFloat(esgData[numKey]) || 0;
      return base > 0 ? parseFloat(((num / base) * 100).toFixed(2)) : 0;
    };
    return {
      ...esgData, mes: esgMes, ano: esgAno,
      diversidade_genero_pct: calc('qtd_mulheres', total),
      diversidade_racial_pct: calc('qtd_negros_pardos_indigenas', total),
      diversidade_pcd_pct: calc('qtd_pcd', total),
      lideranca_feminina_pct: calc('qtd_mulheres_lideranca', totalLid),
      equidade_salarial_gap_pct: (() => {
        const sm = parseFloat(esgData.salario_medio_mulheres) || 0;
        const sh = parseFloat(esgData.salario_medio_homens) || 0;
        return sh > 0 ? parseFloat((((sh - sm) / sh) * 100).toFixed(2)) : 0;
      })(),
    };
  };

  const submitModule = async (modulo, dados) => {
    setLoading(true);
    try {
      await axios.post(`${API}/surveys/${surveyId}/respond`, { modulo, dados }, { headers: getHeaders() });
      toast.success(`Módulo "${MODULE_LABELS[modulo]}" enviado!`);
      setSubmittedModules(prev => [...new Set([...prev, modulo])]);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao enviar'); }
    setLoading(false);
  };

  const addSalaryRow = () => setSalaryData(prev => [...prev, { cargo: '', salario_base: '', remuneracao_variavel: '', qtd_ocupantes: '', horas_extras: '', adicional_noturno: '', adicional_periculosidade: '', adicional_insalubridade: '' }]);
  const updateSalaryRow = (idx, field, value) => setSalaryData(prev => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row));
  const removeSalaryRow = (idx) => setSalaryData(prev => prev.filter((_, i) => i !== idx));

  // Headcount detail helpers
  const headcountDims = survey?.headcount_dimensions || [];
  const addHeadcountRow = () => setHeadcountRows(prev => [...prev, {
    setor: '', cargo: '', tipo_servico: '',
    total_funcionarios: '', mulheres: '', pcd: '', admissoes: '', demissoes: '',
  }]);
  const updateHeadcountRow = (idx, field, value) => setHeadcountRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  const removeHeadcountRow = (idx) => setHeadcountRows(prev => prev.filter((_, i) => i !== idx));

  // Custom remuneration fields from survey
  const surveyCamposRemun = survey?.campos_remuneracao || [];
  const updateOutroRemun = (idx, outroIdx, field, value) => {
    setSalaryData(prev => prev.map((row, i) => {
      if (i !== idx) return row;
      const outros = [...(row.outros_remuneracao || [])];
      outros[outroIdx] = { ...(outros[outroIdx] || {}), [field]: value };
      return { ...row, outros_remuneracao: outros };
    }));
  };

  if (!survey) return <MainLayout title="Responder Pesquisa" userRole={userRole}><div className="flex items-center justify-center py-20 text-slate-400">Carregando...</div></MainLayout>;

  const modules = survey.modulos || [];

  return (
    <MainLayout title={`Responder: ${survey.titulo}`} userRole={userRole}>
      <div className="space-y-6 max-w-4xl" data-testid="responder-pesquisa-page">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
          Preencha as <strong>quantidades absolutas</strong> — os percentuais são calculados automaticamente.
          Indicadores RH e ESG são <strong>mensais</strong>. Remuneração e Benefícios são <strong>anuais</strong>.
        </div>

        <Tabs defaultValue={modules[0]} className="w-full">
          <TabsList className="flex flex-wrap gap-1 h-auto p-1">
            {modules.map(mod => (
              <TabsTrigger key={mod} value={mod} className="text-sm gap-1.5" data-testid={`tab-${mod}`}>
                {submittedModules.includes(mod) && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
                {MODULE_LABELS[mod]}
                <span className="text-[10px] opacity-50 ml-1">{['hr','esg'].includes(mod) ? '(mensal)' : '(anual)'}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {/* ===== HR MENSAL ===== */}
          {modules.includes('hr') && (
            <TabsContent value="hr">
              <Card>
                <CardHeader><CardTitle className="text-base">Indicadores de RH (Mensal)</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <PeriodSelector type="mensal" mes={hrMes} setMes={setHrMes} ano={hrAno} setAno={setHrAno} />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <SimpleField label="Total de Funcionários no mês" fieldKey="total_funcionarios" data={hrData} onChange={hrOnChange} suffix="Base de cálculo · sincronizado com ESG" />
                    <SimpleField label="Admissões no mês" fieldKey="admissoes" data={hrData} onChange={hrOnChange} />
                  </div>
                  {headcountSynced && (
                    <p className="text-[11px] text-emerald-600 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 inline-flex items-center gap-1" data-testid="hr-headcount-synced">
                      <CheckCircle2 className="w-3 h-3" /> Total de pessoas sincronizado com ESG &amp; Pessoas
                    </p>
                  )}

                  <CalcField label="Turnover Geral" numKey="demissoes_total" numLabel="Total desligamentos" denomKey="total_funcionarios" denomLabel="Total funcionários" data={hrData} onChange={hrOnChange} pctLabel="Desligamentos / Total funcionários × 100" />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <CalcField label="Turnover Voluntário" numKey="demissoes_voluntarias" numLabel="Demissões voluntárias" denomKey="total_funcionarios" denomLabel="Total func." data={hrData} onChange={hrOnChange} />
                    <CalcField label="Turnover Involuntário" numKey="demissoes_involuntarias" numLabel="Demissões involuntárias" denomKey="total_funcionarios" denomLabel="Total func." data={hrData} onChange={hrOnChange} />
                  </div>

                  <div className="bg-slate-50 rounded-lg p-3 space-y-2" data-testid="calc-field-absenteismo">
                    <p className="text-xs font-medium text-slate-700">Absenteísmo</p>
                    <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                      <div>
                        <Label className="text-[11px] text-slate-500">Total dias de ausência</Label>
                        <Input type="number" value={hrData.dias_ausencia || ''} onChange={e => hrOnChange('dias_ausencia', e.target.value)} className="h-8 text-sm" data-testid="input-dias_ausencia" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-slate-500">Dias úteis no mês</Label>
                        <Input type="number" value={hrData.dias_uteis || '22'} onChange={e => hrOnChange('dias_uteis', e.target.value)} className="h-8 text-sm" data-testid="input-dias_uteis" />
                      </div>
                      <div className="flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1.5 rounded text-sm font-semibold min-w-[80px] justify-center">
                        <Calculator className="w-3 h-3" />
                        {(() => {
                          const aus = parseFloat(hrData.dias_ausencia) || 0;
                          const total = parseFloat(hrData.total_funcionarios) || 0;
                          const dias = parseFloat(hrData.dias_uteis) || 22;
                          return total > 0 && dias > 0 ? ((aus / (total * dias)) * 100).toFixed(2) + '%' : '—';
                        })()}
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-400">Dias ausência / (Total func. × Dias úteis) × 100</p>
                  </div>

                  <CalcField label="Headcount Feminino" numKey="qtd_feminino" numLabel="Nº mulheres" denomKey="total_funcionarios" denomLabel="Total func." data={hrData} onChange={hrOnChange}
                    syncHint={femaleSynced ? '✓ sincronizado com ESG (Diversidade de Gênero)' : 'também usado em ESG'} />
                  <CalcField label="PCD" numKey="qtd_pcd" numLabel="Nº PCD" denomKey="total_funcionarios" denomLabel="Total func." data={hrData} onChange={hrOnChange}
                    pctLabel="Meta legal: 5% (empresas 100+ func.)"
                    syncHint={pcdSynced ? '✓ sincronizado com ESG (PCD)' : 'também usado em ESG'} />

                  <div className="bg-slate-50 rounded-lg p-3 space-y-2" data-testid="calc-field-treinamento">
                    <p className="text-xs font-medium text-slate-700">Treinamento</p>
                    <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                      <div>
                        <Label className="text-[11px] text-slate-500">Total horas de treinamento</Label>
                        <Input type="number" value={hrData.total_horas_treinamento || ''} onChange={e => hrOnChange('total_horas_treinamento', e.target.value)} className="h-8 text-sm" data-testid="input-total_horas_treinamento" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-slate-500">Total funcionários</Label>
                        <Input type="number" value={hrData.total_funcionarios || ''} disabled className="h-8 text-sm bg-white" />
                      </div>
                      <div className="flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1.5 rounded text-sm font-semibold min-w-[80px] justify-center">
                        <Calculator className="w-3 h-3" />
                        {(() => {
                          const h = parseFloat(hrData.total_horas_treinamento) || 0;
                          const t = parseFloat(hrData.total_funcionarios) || 0;
                          return t > 0 ? (h / t).toFixed(1) + ' h' : '—';
                        })()}
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-400">Total horas / Total funcionários</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <SimpleField label="Tempo médio de recrutamento (dias)" fieldKey="tempo_medio_recrutamento_dias" data={hrData} onChange={hrOnChange} />
                    <SimpleField label="Custo médio de contratação (R$)" fieldKey="custo_medio_contratacao" data={hrData} onChange={hrOnChange} />
                    <SimpleField label="Satisfação dos funcionários (0-100)" fieldKey="satisfacao_funcionarios" data={hrData} onChange={hrOnChange} suffix="Nota da pesquisa interna" />
                    <SimpleField label="Vagas abertas no mês" fieldKey="vagas_abertas" data={hrData} onChange={hrOnChange} />
                  </div>

                  <Button onClick={() => submitModule('hr', buildHrPayload())} disabled={loading} className="gap-2 w-full" data-testid="submit-hr">
                    <Save className="w-4 h-4" /> {submittedModules.includes('hr') ? 'Atualizar' : 'Enviar'} Indicadores RH — {MESES.find(m => m.value === hrMes)?.label}/{hrAno}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* ===== ESG MENSAL ===== */}
          {modules.includes('esg') && (
            <TabsContent value="esg">
              <Card>
                <CardHeader><CardTitle className="text-base">ESG & Pessoas (Mensal)</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <PeriodSelector type="mensal" mes={esgMes} setMes={setEsgMes} ano={esgAno} setAno={setEsgAno} />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <SimpleField label="Total de colaboradores" fieldKey="total_colaboradores" data={esgData} onChange={esgOnChange} suffix="Base para diversidade · sincronizado com RH" />
                    <SimpleField label="Total em cargos de liderança" fieldKey="total_lideranca" data={esgData} onChange={esgOnChange} suffix="Base para liderança feminina" />
                  </div>
                  {headcountSynced && (
                    <p className="text-[11px] text-emerald-600 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 inline-flex items-center gap-1" data-testid="esg-headcount-synced">
                      <CheckCircle2 className="w-3 h-3" /> Total de pessoas sincronizado com Indicadores de RH
                    </p>
                  )}

                  <CalcField label="Diversidade de Gênero" numKey="qtd_mulheres" numLabel="Nº mulheres" denomKey="total_colaboradores" denomLabel="Total colab." data={esgData} onChange={esgOnChange}
                    syncHint={femaleSynced ? '✓ sincronizado com RH (Headcount Feminino)' : 'também usado em RH'} />
                  <CalcField label="Diversidade Racial" numKey="qtd_negros_pardos_indigenas" numLabel="Nº negros/pardos/indígenas" denomKey="total_colaboradores" denomLabel="Total colab." data={esgData} onChange={esgOnChange} />
                  <CalcField label="PCD" numKey="qtd_pcd" numLabel="Nº PCD" denomKey="total_colaboradores" denomLabel="Total colab." data={esgData} onChange={esgOnChange}
                    syncHint={pcdSynced ? '✓ sincronizado com RH (PCD)' : 'também usado em RH'} />
                  <CalcField label="Liderança Feminina" numKey="qtd_mulheres_lideranca" numLabel="Mulheres em liderança" denomKey="total_lideranca" denomLabel="Total liderança" data={esgData} onChange={esgOnChange} />

                  <div className="bg-slate-50 rounded-lg p-3 space-y-2" data-testid="calc-field-equidade">
                    <p className="text-xs font-medium text-slate-700">Equidade Salarial (Gap de gênero)</p>
                    <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                      <div>
                        <Label className="text-[11px] text-slate-500">Salário médio mulheres (R$)</Label>
                        <Input type="number" step="0.01" value={esgData.salario_medio_mulheres || ''} onChange={e => esgOnChange('salario_medio_mulheres', e.target.value)} className="h-8 text-sm" data-testid="input-salario_medio_mulheres" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-slate-500">Salário médio homens (R$)</Label>
                        <Input type="number" step="0.01" value={esgData.salario_medio_homens || ''} onChange={e => esgOnChange('salario_medio_homens', e.target.value)} className="h-8 text-sm" data-testid="input-salario_medio_homens" />
                      </div>
                      <div className="flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1.5 rounded text-sm font-semibold min-w-[80px] justify-center">
                        <Calculator className="w-3 h-3" />
                        {(() => {
                          const sm = parseFloat(esgData.salario_medio_mulheres) || 0;
                          const sh = parseFloat(esgData.salario_medio_homens) || 0;
                          return sh > 0 ? ((((sh - sm) / sh) * 100).toFixed(2)) + '%' : '—';
                        })()}
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-400">(Sal. Homens - Sal. Mulheres) / Sal. Homens × 100 — Quanto menor, melhor</p>
                  </div>

                  <p className="text-xs font-medium text-slate-700 pt-2">Scores ESG (0 a 100)</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <SimpleField label="Score Ambiental" fieldKey="score_ambiental" data={esgData} onChange={esgOnChange} />
                    <SimpleField label="Score Social" fieldKey="score_social" data={esgData} onChange={esgOnChange} />
                    <SimpleField label="Score Governança" fieldKey="score_governanca" data={esgData} onChange={esgOnChange} />
                  </div>

                  <p className="text-xs font-medium text-slate-700 pt-2">Indicadores Ambientais</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <SimpleField label="Emissões CO2 (toneladas)" fieldKey="emissoes_co2_toneladas" data={esgData} onChange={esgOnChange} />
                    <SimpleField label="% Energia renovável" fieldKey="energia_renovavel_pct" data={esgData} onChange={esgOnChange} />
                    <SimpleField label="% Resíduos reciclados" fieldKey="residuos_reciclados_pct" data={esgData} onChange={esgOnChange} />
                  </div>

                  <Button onClick={() => submitModule('esg', buildEsgPayload())} disabled={loading} className="gap-2 w-full" data-testid="submit-esg">
                    <Save className="w-4 h-4" /> {submittedModules.includes('esg') ? 'Atualizar' : 'Enviar'} ESG — {MESES.find(m => m.value === esgMes)?.label}/{esgAno}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* ===== SALARY ANUAL ===== */}
          {modules.includes('salary') && (
            <TabsContent value="salary">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Remuneração por Cargo (Anual)</CardTitle>
                    <Button variant="outline" size="sm" onClick={addSalaryRow} className="text-xs" data-testid="add-salary-row">+ Cargo</Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <PeriodSelector type="anual" ano={salaryAno} setAno={setSalaryAno} />

                  {/* Alert for pending cargo updates */}
                  {pendingCargoUpdates.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="cargo-update-banner">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-amber-800">Cargos atualizados pelo gerenciador</p>
                          <p className="text-xs text-amber-600 mt-1">Os seguintes cargos precisam ser revisados:</p>
                          <div className="flex gap-1.5 mt-1.5 flex-wrap">
                            {pendingCargoUpdates.flatMap(u => u.cargos_atualizados || []).map((c, i) => (
                              <span key={i} className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-medium">{c}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Survey-defined cargo descriptions reference */}
                  {surveyCargos.length > 0 && (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="cargo-descriptions-ref">
                      <p className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1"><Info className="w-3.5 h-3.5" /> Cargos definidos na pesquisa:</p>
                      <div className="space-y-1.5">
                        {surveyCargos.map((c, i) => (
                          <div key={i} className="text-xs">
                            <span className="font-semibold text-slate-800">{c.nome}</span>
                            {c.descricao_sumaria && <span className="text-slate-500 ml-1">— {c.descricao_sumaria}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    {salaryData.map((row, idx) => {
                      const isUpdated = pendingCargoUpdates.some(u => (u.cargos_atualizados || []).includes(row.cargo));
                      const cargoInfo = surveyCargos.find(c => c.nome === row.cargo);
                      return (
                        <div key={idx} className={`border rounded-lg p-3 space-y-2 ${isUpdated ? 'ring-2 ring-amber-300' : 'border-slate-200'}`} data-testid={`salary-entry-${idx}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex-1 mr-2">
                              <Input value={row.cargo} onChange={e => updateSalaryRow(idx, 'cargo', e.target.value)} placeholder="Nome do cargo" className="h-8 text-sm font-medium" list="cargo-options" data-testid={`salary-cargo-${idx}`} />
                              {cargoInfo?.descricao_sumaria && <p className="text-[10px] text-slate-400 mt-0.5 pl-1">{cargoInfo.descricao_sumaria}</p>}
                              {isUpdated && <p className="text-[10px] text-amber-600 font-medium pl-1">Cargo atualizado</p>}
                            </div>
                            <button onClick={() => removeSalaryRow(idx)} className="text-red-400 hover:text-red-600 text-lg px-2">x</button>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            <div>
                              <Label className="text-[10px] text-slate-500">Nº Ocupantes</Label>
                              <Input type="number" value={row.qtd_ocupantes || ''} onChange={e => updateSalaryRow(idx, 'qtd_ocupantes', e.target.value)} placeholder="Qtd" className="h-8 text-sm" data-testid={`salary-qtd-${idx}`} />
                            </div>
                            <div>
                              <Label className="text-[10px] text-slate-500">Salário Base (R$)</Label>
                              <Input type="number" step="0.01" value={row.salario_base} onChange={e => updateSalaryRow(idx, 'salario_base', e.target.value)} placeholder="R$" className="h-8 text-sm" />
                            </div>
                            <div>
                              <Label className="text-[10px] text-slate-500">Rem. Variável (R$)</Label>
                              <Input type="number" step="0.01" value={row.remuneracao_variavel} onChange={e => updateSalaryRow(idx, 'remuneracao_variavel', e.target.value)} placeholder="R$" className="h-8 text-sm" />
                            </div>
                            <div className="flex items-end gap-1 bg-blue-50 rounded p-1.5">
                              <Calculator className="w-3 h-3 text-blue-600 mb-1" />
                              <span className="text-xs font-semibold text-blue-700">
                                Total: R$ {(
                                  (parseFloat(row.salario_base) || 0) +
                                  (parseFloat(row.remuneracao_variavel) || 0) +
                                  (parseFloat(row.horas_extras) || 0) +
                                  (parseFloat(row.adicional_noturno) || 0) +
                                  (parseFloat(row.adicional_periculosidade) || 0) +
                                  (parseFloat(row.adicional_insalubridade) || 0) +
                                  ((row.outros_remuneracao || []).reduce((s, o) => s + (parseFloat(o.valor) || 0), 0))
                                ).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}
                              </span>
                            </div>
                          </div>
                          {/* Remuneration components */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-1 border-t border-slate-100">
                            {REMUN_FIELDS.map(f => (
                              <div key={f.key}>
                                <Label className="text-[10px] text-slate-500">{f.label}</Label>
                                <Input type="number" step="0.01" value={row[f.key] || ''} onChange={e => updateSalaryRow(idx, f.key, e.target.value)} placeholder="R$" className="h-7 text-sm" data-testid={`salary-${f.key}-${idx}`} />
                              </div>
                            ))}
                          </div>
                          {/* Custom remuneration fields */}
                          {(row.outros_remuneracao || []).length > 0 && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-1 border-t border-dashed border-slate-200">
                              {(row.outros_remuneracao || []).map((o, oi) => (
                                <div key={oi}>
                                  <Label className="text-[10px] text-slate-500">{o.descricao} (R$)</Label>
                                  <Input type="number" step="0.01" value={o.valor || ''} onChange={e => updateOutroRemun(idx, oi, 'valor', e.target.value)} placeholder="R$" className="h-7 text-sm" data-testid={`salary-outro-${oi}-${idx}`} />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <datalist id="cargo-options">
                    {surveyCargos.length > 0
                      ? surveyCargos.map(c => <option key={c.nome} value={c.nome} />)
                      : SALARY_ROLES.map(r => <option key={r} value={r} />)
                    }
                  </datalist>
                  <Button onClick={async () => {
                    await submitModule('salary', { ano: salaryAno, cargos: salaryData.filter(r => r.cargo && r.salario_base) });
                    // Dismiss cargo updates after submitting
                    if (pendingCargoUpdates.length > 0) {
                      try {
                        await axios.post(`${API}/surveys/${surveyId}/dismiss-cargo-update`, {}, { headers: getHeaders() });
                        setPendingCargoUpdates([]);
                      } catch (e) { /* silent */ }
                    }
                  }} disabled={loading} className="gap-2 w-full" data-testid="submit-salary">
                    <Save className="w-4 h-4" /> {submittedModules.includes('salary') ? 'Atualizar' : 'Enviar'} Remuneração — {salaryAno}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* ===== BENEFITS ANUAL ===== */}
          {modules.includes('benefits') && (
            <TabsContent value="benefits">
              <Card>
                <CardHeader><CardTitle className="text-base">Benefícios (Anual)</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <PeriodSelector type="anual" ano={benefitsAno} setAno={setBenefitsAno} />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {BENEFIT_FIELDS.map(f => (
                      <SimpleField key={f.key} label={f.label} fieldKey={f.key} data={benefitsData} onChange={(k, v) => setBenefitsData(p => ({ ...p, [k]: v }))} />
                    ))}
                  </div>
                  <Button onClick={() => submitModule('benefits', { ano: benefitsAno, ...benefitsData })} disabled={loading} className="gap-2 w-full" data-testid="submit-benefits">
                    <Save className="w-4 h-4" /> {submittedModules.includes('benefits') ? 'Atualizar' : 'Enviar'} Benefícios — {benefitsAno}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* ===== HEADCOUNT DETALHADO ===== */}
          {modules.includes('headcount_detail') && (
            <TabsContent value="headcount_detail">
              <Card data-testid="headcount-detail-card">
                <CardHeader>
                  <CardTitle className="text-base">Headcount Detalhado</CardTitle>
                  <p className="text-xs text-slate-500 mt-1">
                    Preencha uma linha por combinação de: <strong>{headcountDims.map(d => ({setor:'Setor', cargo:'Cargo', tipo_servico:'Tipo de Serviço'})[d]).join(' / ')}</strong>.
                    Somente as dimensões marcadas pela pesquisa precisam ser preenchidas.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <PeriodSelector type="mensal" mes={headcountMes} setMes={setHeadcountMes} ano={headcountAno} setAno={setHeadcountAno} />

                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          {headcountDims.includes('setor') && <th className="text-left px-2 py-2 font-medium text-slate-700">Setor</th>}
                          {headcountDims.includes('cargo') && <th className="text-left px-2 py-2 font-medium text-slate-700">Cargo</th>}
                          {headcountDims.includes('tipo_servico') && <th className="text-left px-2 py-2 font-medium text-slate-700">Tipo Serviço</th>}
                          <th className="text-right px-2 py-2 font-medium text-slate-700">Total</th>
                          <th className="text-right px-2 py-2 font-medium text-slate-700">Mulheres</th>
                          <th className="text-right px-2 py-2 font-medium text-slate-700">PCD</th>
                          <th className="text-right px-2 py-2 font-medium text-slate-700">Admissões</th>
                          <th className="text-right px-2 py-2 font-medium text-slate-700">Demissões</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {headcountRows.length === 0 && (
                          <tr><td colSpan={9} className="text-center py-4 text-slate-400">Clique em + para adicionar a primeira linha</td></tr>
                        )}
                        {headcountRows.map((row, idx) => (
                          <tr key={idx} className="border-b border-slate-100" data-testid={`hcd-row-${idx}`}>
                            {headcountDims.includes('setor') && (
                              <td className="px-1 py-1">
                                <Input
                                  value={row.setor || ''}
                                  onChange={e => updateHeadcountRow(idx, 'setor', e.target.value)}
                                  placeholder="Ex: Industrial / Linha A"
                                  className="h-8 text-xs min-w-[140px]"
                                  data-testid={`hcd-setor-${idx}`}
                                />
                              </td>
                            )}
                            {headcountDims.includes('cargo') && (
                              <td className="px-1 py-1 min-w-[200px]">
                                <CargoAutocomplete
                                  value={row.cargo || ''}
                                  onChange={v => updateHeadcountRow(idx, 'cargo', v)}
                                  onSelect={c => updateHeadcountRow(idx, 'cargo', c.nome)}
                                  testId={`hcd-cargo-${idx}`}
                                />
                              </td>
                            )}
                            {headcountDims.includes('tipo_servico') && (
                              <td className="px-1 py-1">
                                <Input
                                  value={row.tipo_servico || ''}
                                  onChange={e => updateHeadcountRow(idx, 'tipo_servico', e.target.value)}
                                  placeholder="CLT / PJ / Terceirizado"
                                  className="h-8 text-xs min-w-[120px]"
                                  data-testid={`hcd-tipo-${idx}`}
                                />
                              </td>
                            )}
                            {['total_funcionarios','mulheres','pcd','admissoes','demissoes'].map(f => (
                              <td key={f} className="px-1 py-1">
                                <Input
                                  type="number"
                                  value={row[f] || ''}
                                  onChange={e => updateHeadcountRow(idx, f, e.target.value)}
                                  className="h-8 text-xs text-right w-20"
                                  data-testid={`hcd-${f}-${idx}`}
                                />
                              </td>
                            ))}
                            <td className="px-1 py-1">
                              <button onClick={() => removeHeadcountRow(idx)} className="text-red-400 hover:text-red-600" data-testid={`hcd-remove-${idx}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <Button variant="outline" size="sm" onClick={addHeadcountRow} className="gap-1 text-xs" data-testid="hcd-add-row">
                    <Plus className="w-3 h-3" /> Adicionar linha
                  </Button>

                  <Button
                    onClick={() => submitModule('headcount_detail', { mes: headcountMes, ano: headcountAno, dimensions: headcountDims, rows: headcountRows })}
                    disabled={loading || headcountRows.length === 0}
                    className="gap-2 w-full"
                    data-testid="submit-headcount-detail"
                  >
                    <Save className="w-4 h-4" /> {submittedModules.includes('headcount_detail') ? 'Atualizar' : 'Enviar'} Headcount — {headcountMes}/{headcountAno}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {submittedModules.length === modules.length && modules.length > 0 && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center" data-testid="all-submitted-message">
            <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
            <p className="font-medium text-emerald-700">Todos os módulos foram enviados!</p>
            <Button variant="outline" className="mt-3 text-xs" onClick={() => navigate('/pesquisas')}>Voltar para Pesquisas</Button>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ResponderPesquisa;
